import { useState, useCallback, useRef } from 'react';
import {
  postQuoteRequestMessage,
  getQuoteRequestMessages,
  postQuoteLineMessage,
  getQuoteLineMessages,
  getQuoteLineRemarks,
  updateQuoteLineRemarks,
  getQuoteLinesWithMessages,
  uploadQuoteRequestAttachments,
  getQuoteRequestAttachments,
  uploadQuoteLineAttachments,
  getQuoteLineAttachments,
  deleteAttachment,
} from '../api';

/**
 * Custom hook for managing quote request and line messaging
 * @param {Object} options - Hook options
 * @param {number} [options.requestId] - Quote request ID for request-level operations
 * @param {number} [options.lineId] - Quote line ID for line-level operations
 * @returns {Object} Messaging functions and state
 */
export const useQuoteMessaging = ({ requestId, lineId } = {}) => {
  const [messages, setMessages] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
  });
  const [attachments, setAttachments] = useState([]);

  // Sequence counters to guard against out-of-order responses from overlapping
  // fetches (e.g. an onlyNotes filter toggle racing a push-triggered refresh).
  const requestFetchSeqRef = useRef(0);
  const lineFetchSeqRef = useRef(0);

  // ===============================
  // Quote Request Message Functions
  // ===============================

  /**
   * Post a message to the quote request
   * @param {Object} messageData - Message data
   * @param {string} messageData.body - HTML message content
   * @param {boolean} [messageData.is_note] - Whether this is an internal note
   * @returns {Promise<Object>} Posted message object
   */
  const postRequestMessage = useCallback(
    async (messageData) => {
      if (!requestId) {
        throw new Error('requestId is required for posting request messages');
      }

      setLoading(true);
      setError(null);

      try {
        const response = await postQuoteRequestMessage(requestId, messageData);
        const newMessage = response.data.message;

        // Add new message to the beginning of the list
        setMessages((prev) => [newMessage, ...prev]);

        return newMessage;
      } catch (err) {
        setError(err.response?.data?.error || err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [requestId]
  );

  /**
   * Fetch messages for the quote request
   * @param {Object} [params] - Query parameters
   * @param {number} [params.limit] - Number of messages to fetch
   * @param {number} [params.offset] - Pagination offset
   * @param {boolean} [params.only_notes] - Filter only internal notes
   * @returns {Promise<Array>} Array of messages
   */
  const fetchRequestMessages = useCallback(
    async (params = {}) => {
      if (!requestId) {
        throw new Error('requestId is required for fetching request messages');
      }

      const seq = ++requestFetchSeqRef.current;
      setLoading(true);
      setError(null);

      try {
        const response = await getQuoteRequestMessages(requestId, params);
        const { messages: fetchedMessages, total, limit, offset } = response.data;

        if (seq === requestFetchSeqRef.current) {
          setMessages(fetchedMessages);
          setPagination({ total, limit, offset });
        }

        return fetchedMessages;
      } catch (err) {
        if (seq === requestFetchSeqRef.current) {
          setError(err.response?.data?.error || err.message);
        }
        throw err;
      } finally {
        if (seq === requestFetchSeqRef.current) {
          setLoading(false);
        }
      }
    },
    [requestId]
  );

  // ===============================
  // Quote Line Message Functions
  // ===============================

  /**
   * Post a message to the quote line
   * @param {Object} messageData - Message data
   * @param {string} messageData.body - HTML message content
   * @param {boolean} [messageData.is_note] - Whether this is an internal note
   * @returns {Promise<Object>} Posted message object
   */
  const postLineMessage = useCallback(
    async (messageData) => {
      if (!lineId) {
        throw new Error('lineId is required for posting line messages');
      }

      setLoading(true);
      setError(null);

      try {
        const response = await postQuoteLineMessage(lineId, messageData);
        const newMessage = response.data.message;

        // Add new message to the beginning of the list
        setMessages((prev) => [newMessage, ...prev]);

        return newMessage;
      } catch (err) {
        setError(err.response?.data?.error || err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [lineId]
  );

  /**
   * Fetch messages for the quote line
   * @param {Object} [params] - Query parameters
   * @param {number} [params.limit] - Number of messages to fetch
   * @param {number} [params.offset] - Pagination offset
   * @param {boolean} [params.only_notes] - Filter only internal notes
   * @returns {Promise<Array>} Array of messages
   */
  const fetchLineMessages = useCallback(
    async (params = {}) => {
      if (!lineId) {
        throw new Error('lineId is required for fetching line messages');
      }

      const seq = ++lineFetchSeqRef.current;
      setLoading(true);
      setError(null);

      try {
        const response = await getQuoteLineMessages(lineId, params);
        const { messages: fetchedMessages, total, limit, offset } = response.data;

        if (seq === lineFetchSeqRef.current) {
          setMessages(fetchedMessages);
          setPagination({ total, limit, offset });
        }

        return fetchedMessages;
      } catch (err) {
        if (seq === lineFetchSeqRef.current) {
          setError(err.response?.data?.error || err.message);
        }
        throw err;
      } finally {
        if (seq === lineFetchSeqRef.current) {
          setLoading(false);
        }
      }
    },
    [lineId]
  );

  // ===============================
  // Quote Line Remarks Functions
  // ===============================

  /**
   * Fetch remarks for the quote line
   * @returns {Promise<string>} Remarks text
   */
  const fetchLineRemarks = useCallback(async () => {
    if (!lineId) {
      throw new Error('lineId is required for fetching line remarks');
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getQuoteLineRemarks(lineId);
      const fetchedRemarks = response.data.remarks || '';

      setRemarks(fetchedRemarks);

      return fetchedRemarks;
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [lineId]);

  /**
   * Update remarks for the quote line
   * @param {string} newRemarks - Updated remarks text
   * @returns {Promise<string>} Updated remarks text
   */
  const updateLineRemarks = useCallback(
    async (newRemarks) => {
      if (!lineId) {
        throw new Error('lineId is required for updating line remarks');
      }

      setLoading(true);
      setError(null);

      try {
        const response = await updateQuoteLineRemarks(lineId, newRemarks);
        const updatedRemarks = response.data.remarks;

        setRemarks(updatedRemarks);

        return updatedRemarks;
      } catch (err) {
        setError(err.response?.data?.error || err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [lineId]
  );

  // ===============================
  // Lines with Message Counts
  // ===============================

  /**
   * Fetch all lines with message counts for a quote request
   * @returns {Promise<Array>} Array of lines with message counts
   */
  const fetchLinesWithMessages = useCallback(async () => {
    if (!requestId) {
      throw new Error('requestId is required for fetching lines with messages');
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getQuoteLinesWithMessages(requestId);
      const { lines } = response.data;

      return lines;
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  // ===============================
  // Attachment Management Functions
  // ===============================

  /**
   * Upload attachments to quote request
   * @param {File[]} files - Files to upload
   * @returns {Promise<Array>} Uploaded attachments
   */
  const uploadRequestAttachments = useCallback(
    async (files) => {
      if (!requestId) {
        throw new Error('requestId is required for uploading attachments');
      }

      setLoading(true);
      setError(null);

      try {
        const response = await uploadQuoteRequestAttachments(requestId, files);
        const newAttachments = response.data.attachments || [];

        // Add to attachments list
        setAttachments((prev) => [...newAttachments, ...prev]);

        return newAttachments;
      } catch (err) {
        setError(err.response?.data?.error || err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [requestId]
  );

  /**
   * Fetch attachments for quote request
   * @returns {Promise<Array>} Array of attachments
   */
  const fetchRequestAttachments = useCallback(async () => {
    if (!requestId) {
      throw new Error('requestId is required for fetching attachments');
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getQuoteRequestAttachments(requestId);
      const fetchedAttachments = response.data.attachments || [];

      setAttachments(fetchedAttachments);

      return fetchedAttachments;
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  /**
   * Upload attachments to quote line
   * @param {File[]} files - Files to upload
   * @returns {Promise<Array>} Uploaded attachments
   */
  const uploadLineAttachments = useCallback(
    async (files) => {
      if (!lineId) {
        throw new Error('lineId is required for uploading attachments');
      }

      setLoading(true);
      setError(null);

      try {
        const response = await uploadQuoteLineAttachments(lineId, files);
        const newAttachments = response.data.attachments || [];

        // Add to attachments list
        setAttachments((prev) => [...newAttachments, ...prev]);

        return newAttachments;
      } catch (err) {
        setError(err.response?.data?.error || err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [lineId]
  );

  /**
   * Fetch attachments for quote line
   * @returns {Promise<Array>} Array of attachments
   */
  const fetchLineAttachments = useCallback(async () => {
    if (!lineId) {
      throw new Error('lineId is required for fetching attachments');
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getQuoteLineAttachments(lineId);
      const fetchedAttachments = response.data.attachments || [];

      setAttachments(fetchedAttachments);

      return fetchedAttachments;
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [lineId]);

  /**
   * Delete an attachment
   * @param {number} attachmentId - Attachment ID to delete
   * @returns {Promise<void>}
   */
  const removeAttachment = useCallback(async (attachmentId) => {
    setLoading(true);
    setError(null);

    try {
      await deleteAttachment(attachmentId);

      // Remove from local state
      setAttachments((prev) => prev.filter((att) => att.id !== attachmentId));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // State
    messages,
    remarks,
    loading,
    error,
    pagination,
    attachments,

    // Request message functions
    postRequestMessage,
    fetchRequestMessages,

    // Line message functions
    postLineMessage,
    fetchLineMessages,

    // Line remarks functions
    fetchLineRemarks,
    updateLineRemarks,

    // Attachment functions
    uploadRequestAttachments,
    fetchRequestAttachments,
    uploadLineAttachments,
    fetchLineAttachments,
    removeAttachment,

    // Utility functions
    fetchLinesWithMessages,

    // Manual state setters (for advanced use cases)
    setMessages,
    setRemarks,
    setError,
    setAttachments,
  };
};

export default useQuoteMessaging;
